/**
 * @description Component-TradeChartsMobile
 */
import SynTitleTabs from '@/components/Tabs/SynTitleTabs';
import './index.less';

import OrderBookSection from '@/components/OrderBookSection';
import { CHART_TYPE } from '@/constants/trade';
import { getPairKlineData } from '@/features/chart/actions';
import { useBackendChainConfig } from '@/features/config/hook';
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import { setFundingChartDuration, setPairChartDuration } from '@/features/trade/actions';
import { useChartDuration, useFundingChartDuration } from '@/features/trade/hooks';
import { useAppDispatch } from '@/hooks';
import { useChainId } from '@/hooks/web3/useChain';
import TvChart from '@/pages/components/TradingViewPro';
import FundingChart from '@/pages/trade/CoinPriceChart/FundingChart';
import PriceDepthChart from '@/pages/trade/CoinPriceChart/PriceDepthChart';
import { FundingChartInterval, KlineInterval } from '@synfutures/sdks-perp-datasource';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TradePairInfoMobile from './TradePairInfoMobile';

interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
}
const TradeChartsMobile: FC<IPropTypes> = function ({}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const chainId = useChainId();
  const dappConfig = useBackendChainConfig(chainId);
  const [tab, setTab] = useState(CHART_TYPE.PRICE);

  const tabList = useMemo(
    () => [
      { label: t('common.tradePage.price'), vale: CHART_TYPE.PRICE, key: CHART_TYPE.PRICE },
      { label: t('common.orderBook.orderB'), vale: CHART_TYPE.ORDERBOOK, key: CHART_TYPE.ORDERBOOK },
      { label: t('common.tradePage.depth'), vale: CHART_TYPE.DEPTH, key: CHART_TYPE.DEPTH },
      { label: t('common.tradePage.funding'), vale: CHART_TYPE.FUNDING, key: CHART_TYPE.FUNDING },
      { label: t('common.tradePage.info'), vale: CHART_TYPE.INFO, key: CHART_TYPE.INFO },
    ],
    [t],
  );
  const chartDuration = useChartDuration(chainId);
  const fundingChartDuration = useFundingChartDuration(chainId);
  const setChartDuration = useCallback(
    (duration: KlineInterval) => {
      chainId && dispatch(setPairChartDuration({ chainId, chartDuration: duration }));
    },
    [chainId, dispatch],
  );
  const setFundingChartDurationCB = useCallback(
    (duration: FundingChartInterval) => {
      chainId && dispatch(setFundingChartDuration({ chainId, chartDuration: duration }));
    },
    [chainId, dispatch],
  );

  const currentPair = useCurrentPairFromUrl(chainId);

  useEffect(() => {
    const defaultDuration =
      (dappConfig?.trade.fundingChartInterval.default as FundingChartInterval) || FundingChartInterval.HOUR;
    setFundingChartDurationCB(defaultDuration);
    return () => {
      setFundingChartDurationCB(defaultDuration);
    };
  }, [dappConfig?.trade.fundingChartInterval.default, setFundingChartDurationCB]);
  useEffect(() => {
    const defaultDuration = (dappConfig?.trade.klineInterval.default as KlineInterval) || KlineInterval.HOUR;
    setChartDuration(defaultDuration);
    return () => {
      const defaultDuration = (dappConfig?.trade.klineInterval.default as KlineInterval) || KlineInterval.HOUR;
      setChartDuration(defaultDuration);
    };
  }, [chainId, dappConfig?.trade.klineInterval.default, setChartDuration, currentPair]);

  useEffect(() => {
    if (currentPair && chainId) {
      try {
        dispatch(
          getPairKlineData({
            chainId,
            instrumentAddr: currentPair.rootInstrument.id,
            expiry: currentPair.expiry,
            chartDuration,
          }),
        );
      } catch (e) {
        console.log('🚀 ~ file: index.tsx:122 ~ useEffect ~ e:', e);
      }
    }
  }, [currentPair?.id, chainId, chartDuration]);

  useEffect(() => {
    setTab(CHART_TYPE.PRICE);
  }, [chainId, currentPair?.id]);

  return (
    <div className="syn-trade-charts-mobile">
      <div className="syn-trade-charts-mobile-header">
        {' '}
        <SynTitleTabs
          tabsStyle={{ gap: 16 }}
          varient="solid"
          activeKey={tab}
          onClick={(key) => setTab(key as CHART_TYPE)}
          items={tabList}
        />
      </div>
      <div className="syn-trade-charts-mobile-content">
        {tab === CHART_TYPE.PRICE && <TvChart />}
        {tab === CHART_TYPE.FUNDING && (
          <FundingChart chainId={chainId} currentPair={currentPair} chartDuration={fundingChartDuration} />
        )}
        {tab === CHART_TYPE.DEPTH && <PriceDepthChart pair={currentPair} />}
        {tab === CHART_TYPE.INFO && <TradePairInfoMobile currentPair={currentPair} />}
        {tab === CHART_TYPE.ORDERBOOK && <OrderBookSection currentPair={currentPair} />}
      </div>
    </div>
  );
};

export default TradeChartsMobile;
